import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './components/common/ProtectedRoute';
import Loading from './components/common/Loading';
import ErrorBoundary from './components/common/ErrorBoundary';
import AppLayout from './components/layouts/AppLayout';
import SignedInRedirect from './components/auth/SignedInRedirect';
import { useAuth } from './contexts/AuthContext';
import { useCanvasData } from './contexts/CanvasDataContext';
import { useAppMode } from './contexts/AppModeContext';
import lazyWithRetry from './utils/lazyWithRetry';

// Lazy load components for better performance with retry mechanism
const Dashboard = lazyWithRetry(() => import('./components/dashboard/Dashboard'));
const CourseDetails = lazyWithRetry(() => import('./components/course/CourseDetails'));
const Login = lazyWithRetry(() => import('./components/auth/Login'));
const Register = lazyWithRetry(() => import('./components/auth/Register'));
const TokenForm = lazyWithRetry(() => import('./components/auth/TokenForm'));
const GuestMode = lazyWithRetry(() => import('./components/guest/GuestMode'));
const GuestDashboard = lazyWithRetry(() => import('./components/guest/GuestDashboard'));
const Profile = lazyWithRetry(() => import('./components/profile/Profile'));
const GradeSnapshots = lazyWithRetry(() => import('./components/snapshots/GradeSnapshots'));
const SnapshotComparison = lazyWithRetry(() => import('./components/snapshots/SnapshotComparison'));
const ImportSnapshot = lazyWithRetry(() => import('./components/snapshots/ImportSnapshot'));
const OfflineMode = lazyWithRetry(() => import('./components/offline/OfflineMode'));
const ActionPlanPage = lazyWithRetry(() => import('./components/dashboard/ActionPlanPage'));
const PrivacyCenter = lazyWithRetry(() => import('./components/compliance/PrivacyCenter'));
const NotFound = lazyWithRetry(() => import('./components/common/NotFound'));
const ConfirmEmail = lazyWithRetry(() => import('./components/auth/ConfirmEmail'));

const AppRoutes = () => {
  const { currentUser, hasCanvasCredentials, isGuestMode, loading: authLoading } = useAuth();
  const { initializing: canvasLoading } = useCanvasData();
  const { coursesMode } = useAppMode();

  // Don't render routes until both auth and canvas contexts are initialized
  if (authLoading || canvasLoading) {
    return <Loading message="Loading application..." />;
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<Loading message="Loading page..." />}>
        <AppLayout>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={
              currentUser ? <SignedInRedirect /> : <Login />
            } />

            <Route path="/register" element={
              currentUser ? <Navigate to="/action-plan" replace /> : <Register />
            } />

            <Route path="/guest" element={<GuestMode />} />

            <Route path="/confirm-email" element={<ConfirmEmail />} />

            {/* Guest routes */}
            <Route path="/guest/dashboard" element={
              <ProtectedRoute allowGuest={true} requireCanvas={false}>
                <GuestDashboard />
              </ProtectedRoute>
            } />

            {/* Auth-only routes */}
            <Route path="/token-setup" element={
              !currentUser ? <Navigate to="/login" replace /> :
              hasCanvasCredentials() ? <Navigate to="/action-plan" replace /> :
              <TokenForm />
            } />

            <Route path="/profile/*" element={
              <ProtectedRoute allowGuest={false}>
                <Profile />
              </ProtectedRoute>
            } />

            <Route path="/privacy" element={
              <ProtectedRoute allowGuest={false}>
                <PrivacyCenter />
              </ProtectedRoute>
            } />

            <Route path="/snapshots" element={
              <ProtectedRoute requireCanvas={true} allowGuest={true}>
                <GradeSnapshots />
              </ProtectedRoute>
            } />

            {/* Task 6.0e. No requireCanvas: entering offline mode is what a
                user does when they have no working Canvas credentials. */}
            <Route path="/offline" element={
              <ProtectedRoute>
                <OfflineMode />
              </ProtectedRoute>
            } />

            <Route path="/snapshots/import" element={
              <ProtectedRoute>
                <ImportSnapshot />
              </ProtectedRoute>
            } />

            <Route path="/compare" element={
              <ProtectedRoute requireCanvas={true} allowGuest={true}>
                <SnapshotComparison />
              </ProtectedRoute>
            } />

            {/* Protected routes allowing both auth and guest */}
            <Route path="/dashboard" element={
              <ProtectedRoute requireCanvas={true} allowGuest={true}>
                <Dashboard />
              </ProtectedRoute>
            } />

            <Route path="/action-plan" element={
              <ProtectedRoute requireCanvas={true} allowGuest={true}>
                <ActionPlanPage />
              </ProtectedRoute>
            } />

            <Route path="/course/:courseId" element={
              <ProtectedRoute requireCanvas={true} allowGuest={true}>
                <CourseDetails />
              </ProtectedRoute>
            } />

            {/* Default route */}
            <Route path="/" element={
              (() => {
                // Demo mode takes priority - always go to guest dashboard
                if (coursesMode === 'demo') {
                  return <Navigate to="/guest/dashboard" replace />;
                }

                if (currentUser) {
                  if (hasCanvasCredentials()) {
                    // Task 5.1: authenticated landing is the cross-course
                    // action plan, not the course grid. /dashboard (the
                    // grid) stays one click away via the sidebar.
                    return <Navigate to="/action-plan" replace />;
                  } else {
                    return <Navigate to="/token-setup" replace />;
                  }
                }

                if (isGuestMode()) {
                  return <Navigate to="/guest/dashboard" replace />;
                }

                return <Navigate to="/guest" replace />;
              })()
            } />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </AppLayout>
      </Suspense>
    </ErrorBoundary>
  );
};

export default AppRoutes;